import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, Facebook, Instagram, MessageCircle, Heart, MapPin, Headset } from 'lucide-react';
import './App.css';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Team from './pages/Team';
import Stories from './pages/Stories';
import Testimonials from './pages/Testimonials';
import AdminChats from './pages/AdminChats';
import AdminStories from './pages/AdminStories';
import AdminTestimonials from './pages/AdminTestimonials';
import AdminAppointments from './pages/AdminAppointments';
import ChangePin from './pages/ChangePin';
import { Toaster } from './components/ui/sonner';
import { contactInfo } from './data/mock';
import ChatWidget from './components/ChatWidget';
import ChatButton from './components/ChatButton';

const Header = ({ onChatOpen }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/stories', label: 'Stories' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/team', label: 'Our Team' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-blue-100 shadow-sm">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-400 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-1 hover:text-blue-100 transition-colors">
                <Phone size={14} />
                <span>{contactInfo.phone}</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onChatOpen}
                className="hover:text-blue-100 transition-colors relative group flex items-center gap-1"
                title="Live Chat - 24/7 Support"
              >
                <Headset size={16} />
                <span className="text-xs hidden sm:inline">Live Chat</span>
                <span className="absolute -top-1 -right-1 bg-green-500 w-2 h-2 rounded-full animate-pulse"></span>
              </button>
              <span className="text-blue-200">|</span>
              <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-100 transition-colors" title="WhatsApp">
                <MessageCircle size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_palliative-portal/artifacts/e4qdyyq0_image.png" 
              alt="Dard Satya Logo" 
              className="h-16 w-16 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Dard Satya</h1>
              <p className="text-xs text-blue-600 italic">{contactInfo.slogan}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                  isActive(link.path) ? 'text-blue-600' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-blue-500 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-blue-600 transition-all hover:shadow-lg"
            >
              Book Consultation
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-700 hover:text-blue-600 transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-blue-100">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 px-4 text-sm font-medium transition-colors hover:bg-blue-50 ${
                  isActive(link.path) ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="block mt-2 mx-4 text-center bg-blue-500 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-blue-600 transition-all"
            >
              Book Consultation
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_palliative-portal/artifacts/e4qdyyq0_image.png" 
                alt="Dard Satya" 
                className="h-12 w-12"
              />
              <h3 className="text-xl font-bold">Dard Satya</h3>
            </div>
            <p className="text-gray-400 text-sm italic mb-2">{contactInfo.slogan}</p>
            <p className="text-gray-400 text-sm">
              Providing compassionate palliative care for cancer and non-cancer terminal illnesses.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4 text-blue-400">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-gray-400 hover:text-blue-400 transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-blue-400 transition-colors">About Us</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-blue-400 transition-colors">Services</Link></li>
              <li><Link to="/team" className="text-gray-400 hover:text-blue-400 transition-colors">Our Team</Link></li>
              <li><Link to="/faq" className="text-gray-400 hover:text-blue-400 transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-4 text-blue-400">Our Services</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>Cancer Pain Management</li>
              <li>Neuro Palliative Care</li>
              <li>Stroke & Brain Injury Care</li>
              <li>Dementia Care</li>
              <li>Hospice Care</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-4 text-blue-400">Connect With Us</h4>
            <div className="space-y-3 text-sm">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors">
                <Phone size={16} />
                <span>{contactInfo.phone}</span>
              </a>
              <div className="flex gap-4 mt-4">
                <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="bg-green-600 p-2 rounded-full hover:bg-green-700 transition-colors">
                  <MessageCircle size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-6 text-center">
          <p className="text-gray-500 text-xs">
            © {new Date().getFullYear()} Dard Satya. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    // Auto-popup chat after 2-3 seconds for first-time visitors
    const hasAutoOpened = localStorage.getItem('chatAutoOpened');
    
    if (!hasAutoOpened) {
      const timer = setTimeout(() => {
        setIsChatOpen(true);
        setAutoOpened(true);
      }, 2500); // 2.5 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  const handleChatOpen = () => {
    setIsChatOpen(true);
    setAutoOpened(false);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <ChatWidgetWrapper 
          isChatOpen={isChatOpen}
          autoOpened={autoOpened}
          onChatOpen={handleChatOpen}
          onChatClose={handleChatClose}
        />
      </BrowserRouter>
    </div>
  );
}

// Wrapper component to access useLocation
function ChatWidgetWrapper({ isChatOpen, autoOpened, onChatOpen, onChatClose }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <Header onChatOpen={onChatOpen} />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home onChatOpen={onChatOpen} />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:id" element={<ServiceDetail />} />
          <Route path="/stories" element={<Stories />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/team" element={<Team />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact onChatOpen={onChatOpen} />} />
          <Route path="/admin/chats" element={<AdminChats />} />
          <Route path="/admin/appointments" element={<AdminAppointments />} />
          <Route path="/admin/stories" element={<AdminStories />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/change-pin" element={<ChangePin />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-right" />
      
      {/* Chat Components - Hidden on admin routes */}
      {!isAdminRoute && (
        <>
          {!isChatOpen && <ChatButton onClick={onChatOpen} />}
          <ChatWidget 
            isOpen={isChatOpen} 
            onClose={onChatClose}
            autoOpened={autoOpened}
          />
        </>
      )}
    </>
  );
}

export default App;
